import React, { useState, useRef, useEffect } from 'react';
import { View, Image, TouchableOpacity, Text, StyleSheet, Dimensions, Modal, Platform } from 'react-native';
import AudioPlayer from './AudioPlayer';

const windowWidth = Dimensions.get('window').width;
const windowHeight = Dimensions.get('window').height;

const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return 'image';
  }
  
  // PDF
  if (extension === 'pdf') {
    return 'pdf';
  }
  
  // Audio types
  if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
    return 'audio';
  }
  
  // Video types
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  }
  
  // Default to file
  return 'file';
};

export function MediaViewer({ visible, onClose, media, currentIndex, onNavigate }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  
  // Convert media object to array format
  const mediaArray = React.useMemo(() => {
    if (Array.isArray(media)) {
      return media;
    } else if (typeof media === 'object' && media !== null) {
      return Object.entries(media).map(([fileName, url]) => ({
        id: fileName,
        name: fileName,
        url: url,
        type: getFileType(fileName)
      }));
    }
    return [];
  }, [media]);

  const currentMedia = mediaArray[currentIndex];
  const currentFileType = currentMedia ? getFileType(currentMedia.name) : 'image';
  
  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : mediaArray.length - 1;
    onNavigate(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < mediaArray.length - 1 ? currentIndex + 1 : 0;
    onNavigate(newIndex);
  };

  const audioRef = useRef(null);

  const handlePlayPause = () => {
    if (currentFileType === 'video') {
      if (isVideoPlaying) {
        videoRef.current?.pause();
        setIsVideoPlaying(false);
      } else {
        if (isMuted) setIsMuted(false);
        videoRef.current?.play();
        setIsVideoPlaying(true);
      }
    } else if (currentFileType === 'audio') {
      const audio = audioRef.current;
      if (!audio) return;
      if (!audio.paused) {
        audio.pause();
      } else {
        audio.play();
      }
    }
  };

  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const handleDurationChange = (dur) => {
    setDuration(dur);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Reset state when media changes
  useEffect(() => {
    setIsVideoPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsMuted(true); // Reset mute state for new videos
  }, [currentIndex]);

  // Handle video auto-play when video loads
  useEffect(() => {
    if (currentFileType === 'video' && videoRef.current) {
      const video = videoRef.current;
      
      const handleCanPlay = () => {
        console.log('Video can play, attempting auto-play');
        video.play().then(() => {
          console.log('Video auto-play started');
          setIsVideoPlaying(true);
        }).catch(err => {
          console.log('Video auto-play failed:', err);
          // Auto-play failed, show play button
          setIsVideoPlaying(false);
        });
      };

      video.addEventListener('canplay', handleCanPlay);
      
      return () => {
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [currentIndex, currentFileType]);

  // Pause audio and video when modal closes
  useEffect(() => {
    if (!visible) {
      // Force pause any playing audio when modal closes
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        if (!audio.paused) {
          audio.pause();
        }
      });
      
      // Force pause any playing video when modal closes
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(video => {
        if (!video.paused) {
          video.pause();
        }
      });
    }
  }, [visible]);

  const renderMediaContent = () => {
    if (!currentMedia) return null;

    switch (currentFileType) {
      case 'image':
        return (
          <View style={styles.imageWrapper}>
            <Image
              source={{ uri: currentMedia.url }}
              style={styles.mediaContent}
              resizeMode="contain"
            />
            {(currentMedia.description || currentMedia.caption) ? (
              <View style={styles.captionContainer}>
                <Text style={styles.captionText}>{currentMedia.description || currentMedia.caption}</Text>
              </View>
            ) : null}
          </View>
        );

      case 'audio':
        return (
          <View style={[styles.defaultContainer, { padding: 80, width: '100%' }]}> 
            {Platform.OS === 'web' ? (
              <audio ref={audioRef} src={currentMedia.url} controls style={{ width: '90%' }} autoPlay />
            ) : (
              <AudioPlayer url={currentMedia.url} isTabOpen={visible} autoPlay={true} />
            )}
          </View>
        );

      case 'video':
        return (
          <View style={styles.videoContainer}>
            <video
              ref={videoRef}
              src={currentMedia.url}
              style={styles.mediaContent}
              controls={false}
              autoPlay={true}
              muted={isMuted}
              playsInline={true}
              onTimeUpdate={(e) => handleTimeUpdate(e.target.currentTime)}
              onLoadedMetadata={(e) => handleDurationChange(e.target.duration)}
              onEnded={() => setIsVideoPlaying(false)}
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
            />
            {!isVideoPlaying && (
              <TouchableOpacity style={styles.videoPlayOverlay} onPress={handlePlayPause}>
                <Text style={styles.videoPlayButton}>▶️</Text>
              </TouchableOpacity>
            )}
            <View style={styles.videoTimeOverlay}>
              <Text style={styles.timeText}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </Text>
            </View>
          </View>
        );

      case 'pdf':
        return (
          <View style={styles.defaultContainer}>
            <Text style={styles.defaultIcon}>📄</Text>
            <Text style={styles.defaultTitle}>PDF Document</Text>
            <Text style={styles.defaultFileName}>{currentMedia.name}</Text>
            <TouchableOpacity 
              style={styles.defaultButton}
              onPress={() => window.open(currentMedia.url, '_blank')}
            >
              <Text style={styles.defaultButtonText}>Open PDF</Text>
            </TouchableOpacity>
          </View>
        );

      default:
        return (
          <View style={styles.fileContainer}>
            <Text style={styles.fileIcon}>📄</Text>
            <Text style={styles.fileName}>{currentMedia.name}</Text>
            <TouchableOpacity 
              style={styles.downloadButton}
              onPress={() => window.open(currentMedia.url, '_blank')}
            >
              <Text style={styles.downloadButtonText}>Download</Text>
            </TouchableOpacity>
          </View>
        );
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalContainer}>
        <TouchableOpacity style={styles.closeButton} onPress={onClose}>
          <Text style={styles.closeButtonText}>×</Text>
        </TouchableOpacity>

        <View style={styles.container}>
          {/* Navigation arrows */}
          {mediaArray.length > 1 && (
            <>
              <TouchableOpacity style={[styles.navButton, styles.navButtonLeft]} onPress={handlePrevious}>
                <Text style={styles.navButtonText}>←</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.navButton, styles.navButtonRight]} onPress={handleNext}>
                <Text style={styles.navButtonText}>→</Text>
              </TouchableOpacity>
            </>
          )}

          {/* Media content */}
          {renderMediaContent()}

          {/* Media counter */}
          {mediaArray.length > 1 && (
            <View style={styles.counter}>
              <Text style={styles.counterText}>
                {currentIndex + 1} / {mediaArray.length}
              </Text>
            </View>
          )}

          {/* Media type indicator */}
          <View style={styles.typeIndicator}>
            <Text style={styles.typeText}>
              {currentFileType.toUpperCase()}
            </Text>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: '#000000',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  imageWrapper: {
    flex: 1,
    width: '100%',
    position: 'relative',
  },
  mediaContent: {
    width: windowWidth,
    height: windowHeight,
  },
  captionContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  captionText: {
    color: '#ffffff',
    fontSize: 14,
    lineHeight: 20,
  },
  closeButton: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 2,
    width: 40,
    height: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  navButton: {
    position: 'absolute',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  navButtonLeft: {
    left: 20,
  },
  navButtonRight: {
    right: 20,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 'bold',
  },
  counter: {
    position: 'absolute',
    bottom: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    padding: 8,
    borderRadius: 15,
  },
  counterText: {
    color: '#ffffff',
    fontSize: 14,
  },
  typeIndicator: {
    position: 'absolute',
    top: 40,
    left: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
  },
  typeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  timeText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
  },
  // Video styles
  videoContainer: {
    flex: 1,
    width: '100%',
    position: 'relative',
  },
  videoPlayOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 1,
  },
  videoPlayButton: {
    fontSize: 80,
    color: '#ffffff',
  },
  videoTimeOverlay: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
    zIndex: 2,
  },
  // Default styles for unsupported media types
  defaultContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 40,
  },
  defaultIcon: {
    fontSize: 80,
    marginBottom: 20,
  },
  defaultTitle: {
    color: '#ffffff',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'center',
  },
  defaultFileName: {
    color: '#ffffff',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 30,
    opacity: 0.8,
  },
  defaultButton: {
    backgroundColor: '#4e7de0',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  defaultButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  // File styles
  fileContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  fileIcon: {
    fontSize: 80,
    marginBottom: 20,
  },
  fileName: {
    color: '#ffffff',
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 30,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
  },
  downloadButton: {
    backgroundColor: '#4e7de0',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  downloadButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
}); 